// Email validation and parsing utilities for bulk user import

// Dangerous domains that should trigger warnings
const DANGEROUS_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'yandex.com',
  'zoho.com',
  'gmx.com',
  'tutanota.com',
  'fastmail.com',
  'mail.ru',
];

// RFC 5322 compliant email regex
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// RFC 5322 format regex for extracting email from "Name <email@domain.com>"
const RFC_5322_REGEX =
  /^(?:([^<>()[\]\\.,;:\s@"]+(?:\.[^<>()[\]\\.,;:\s@"]+)*|"(?:[^\\"]|\\.)*")\s*)?<?([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)>?$/;

export interface ParsedEmail {
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  description?: string;
  /** Role picked in the review step (annotation only - createUser does not take a role yet). */
  roleId?: string;
  valid: boolean;
  error?: string;
}

export interface EmailDomainWarning {
  domain: string;
  count: number;
  emails: string[];
}

/**
 * Validates if an email address is valid according to RFC 5322
 */
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Attempts to extract first and last name from email address local part
 * Examples:
 *   john.doe@company.com -> { firstName: "John", lastName: "Doe" }
 *   jane_smith@company.com -> { firstName: "Jane", lastName: "Smith" }
 *   bob@company.com -> { firstName: "Bob" }
 */
export function extractNameFromEmail(email: string): {firstName?: string; lastName?: string} {
  const localPart = email.split('@')[0];
  if (!localPart) return {};

  // Replace common separators with spaces
  const normalized = localPart.replace(/[._-]/g, ' ').trim();

  const parts = normalized.split(/\s+/);

  if (parts.length >= 2) {
    return {
      firstName: capitalize(parts[0]),
      lastName: parts.slice(1).map(capitalize).join(' '),
    };
  } else if (parts.length === 1) {
    return {
      firstName: capitalize(parts[0]),
    };
  }

  return {};
}

/**
 * Capitalizes first letter of a string
 */
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Checks if a domain is considered dangerous (free email provider)
 */
export function isDangerousDomain(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();
  return DANGEROUS_DOMAINS.includes(domain);
}

/**
 * Extracts email from RFC 5322 format or plain email
 * Examples:
 *   "John Doe <john@example.com>" -> { email: "john@example.com", name: "John Doe" }
 *   "john@example.com" -> { email: "john@example.com" }
 */
export function parseEmailAddress(input: string): ParsedEmail {
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      email: '',
      valid: false,
      error: 'Empty input',
    };
  }

  // First, check if it's a plain email (no angle brackets)
  if (isValidEmail(trimmed) && !trimmed.includes('<') && !trimmed.includes('>')) {
    // Try to extract name from email address
    const {firstName, lastName} = extractNameFromEmail(trimmed);
    const fullName = [firstName, lastName].filter(Boolean).join(' ') || undefined;

    return {
      email: trimmed,
      firstName,
      lastName,
      name: fullName,
      valid: true,
    };
  }

  // Try RFC 5322 format with angle brackets: "Name <email@domain.com>"
  const rfc5322Match = trimmed.match(RFC_5322_REGEX);

  if (rfc5322Match) {
    const name = rfc5322Match[1]?.replace(/^"|"$/g, '').trim();
    const email = rfc5322Match[2]?.trim();

    // Only treat as RFC 5322 format if there's actually a name part AND angle brackets
    if (email && isValidEmail(email) && (trimmed.includes('<') || trimmed.includes('>'))) {
      // Try to split name into first and last name
      let firstName: string | undefined;
      let lastName: string | undefined;

      if (name) {
        const nameParts = name.split(/\s+/);
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          firstName = nameParts[0];
        }
      }

      return {
        email,
        name: name || undefined,
        firstName,
        lastName,
        valid: true,
      };
    }
  }

  return {
    email: trimmed,
    valid: false,
    error: 'Invalid email format',
  };
}

/**
 * Auto-detects separator in a string of emails
 * Returns the most likely separator: newline, comma, semicolon, or space
 */
export function autoDetectSeparator(text: string): string {
  const separatorCounts = {
    '\n': (text.match(/\n/g) || []).length,
    ',': (text.match(/,/g) || []).length,
    ';': (text.match(/;/g) || []).length,
    ' ': (text.match(/\s+/g) || []).length,
  };

  // Prioritize newline, then comma, semicolon, and space
  const priority = ['\n', ',', ';', ' '];

  let maxCount = 0;
  let detectedSeparator = '\n';

  for (const sep of priority) {
    if (separatorCounts[sep as keyof typeof separatorCounts] > maxCount) {
      maxCount = separatorCounts[sep as keyof typeof separatorCounts];
      detectedSeparator = sep;
    }
  }

  return detectedSeparator;
}

/**
 * Splits text by multiple separators
 */
export function splitByMultipleSeparators(text: string, separators: string[]): string[] {
  // Create a regex pattern that matches any of the separators
  const pattern = separators
    .map((sep) => {
      // Escape special regex characters
      return sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('|');

  const regex = new RegExp(pattern, 'g');

  // Split by the pattern and filter out empty strings
  return text
    .split(regex)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Parses a bulk email string and returns array of parsed emails
 */
export function parseEmailList(text: string, separators: string[] = ['\n']): ParsedEmail[] {
  const parts = splitByMultipleSeparators(text, separators);
  return parts.map((part) => parseEmailAddress(part));
}

/**
 * Groups parsed emails by domain
 */
export function groupEmailsByDomain(emails: ParsedEmail[]): Map<string, ParsedEmail[]> {
  const grouped = new Map<string, ParsedEmail[]>();

  emails.forEach((parsedEmail) => {
    if (parsedEmail.valid) {
      const domain = parsedEmail.email.split('@')[1].toLowerCase();
      if (!grouped.has(domain)) {
        grouped.set(domain, []);
      }
      grouped.get(domain)!.push(parsedEmail);
    }
  });

  return grouped;
}

/**
 * Identifies dangerous domains in the list
 */
export function findDangerousDomains(emails: ParsedEmail[]): EmailDomainWarning[] {
  const warnings: EmailDomainWarning[] = [];
  const domainGroups = groupEmailsByDomain(emails);

  domainGroups.forEach((domainEmails, domain) => {
    if (DANGEROUS_DOMAINS.includes(domain)) {
      warnings.push({
        domain,
        count: domainEmails.length,
        emails: domainEmails.map((e) => e.email),
      });
    }
  });

  return warnings.sort((a, b) => b.count - a.count);
}

/**
 * Finds duplicate emails in the list
 */
export function findDuplicateEmails(emails: ParsedEmail[]): Map<string, number> {
  const emailCounts = new Map<string, number>();

  emails.forEach((parsed) => {
    if (parsed.valid) {
      const email = parsed.email.toLowerCase();
      emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    }
  });

  // Filter to only duplicates
  const duplicates = new Map<string, number>();
  emailCounts.forEach((count, email) => {
    if (count > 1) {
      duplicates.set(email, count);
    }
  });

  return duplicates;
}

/**
 * Gets statistics about the email list
 */
export function getEmailListStats(emails: ParsedEmail[]): {
  total: number;
  valid: number;
  invalid: number;
  withNames: number;
  uniqueDomains: number;
  dangerousDomains: number;
  duplicates: number;
} {
  const valid = emails.filter((e) => e.valid);
  const withNames = valid.filter((e) => e.name);
  const domains = groupEmailsByDomain(emails);
  const dangerousWarnings = findDangerousDomains(emails);
  const duplicates = findDuplicateEmails(emails);

  return {
    total: emails.length,
    valid: valid.length,
    invalid: emails.length - valid.length,
    withNames: withNames.length,
    uniqueDomains: domains.size,
    dangerousDomains: dangerousWarnings.reduce((sum, w) => sum + w.count, 0),
    duplicates: duplicates.size,
  };
}
